import { ActionContext, Module } from "vuex";

import ProjectsApi, { getProjectsApi } from "@/api/ProjectsApi";
import { formatUSDate } from "@/helpers/dates";
import { RootState } from "@/store";
import { PlanCompact, ProjectCompact, ProjectCompactAPI } from "@/types/comparison";
import { Project, Scenario } from "@/types/project";

export type ComparisonHistoryEntity = {
  comparison_id: string;
  project_id: string;
  project: string;
  businessGoal: string;
  plan: string;
  scenarios: string;
  dateOfComparison: string;
};

export type ComparisonApiEntity = {
  comparison_id: string;
  comparison_timestamp: string;
  project_id: string;
  business_goal_id: string;
  plan_id: string;
  scenario_ids: string[];
};

export interface PlanListState {
  loading: boolean;
  projectsList: ProjectCompact[];
  comparisonHistory: ComparisonHistoryEntity[];
  plansList: PlanCompact[];
  selectedScenarios: Scenario[];
  projectDetailed: Project;
}

const initialState: PlanListState = {
  loading: false,
  projectsList: [],
  plansList: [],
  selectedScenarios: [],
  comparisonHistory: [],
  projectDetailed: {} as Project,
};

const planList: Module<PlanListState, RootState> = {
  namespaced: true,

  state: initialState,

  mutations: {
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setProjectList(state, projects: ProjectCompact[]) {
      state.projectsList = projects;
    },
    setComparisonHistory(state, history: ComparisonHistoryEntity[]) {
      state.comparisonHistory = history;
    },
    setProjectDetailed(state, project: Project) {
      state.projectDetailed = project;
    },
    setPlansList(state, plans: PlanCompact[]) {
      state.plansList = plans;
    },
    setSelectedScenarios(state, scenarios: Scenario[]) {
      state.selectedScenarios = scenarios;
    },
  },

  actions: {
    async initialSetup(ctx: ActionContext<PlanListState, RootState>) {
      try {
        ctx.commit("setLoading", true);
        const api = new ProjectsApi();

        const [candidatesResp, comparisonsResp] = await Promise.all([
          api.getComparisonCandidates(),
          api.getScenarioComparisons(),
        ]);

        const projectsApi = candidatesResp as ProjectCompactAPI;
        const projects: ProjectCompact[] = Array.isArray(projectsApi?.data) ? projectsApi.data : [];
        ctx.state.projectsList = projects;

        ctx.state.plansList = projects.reduce((acc: PlanCompact[], p: ProjectCompact) => {
          (p.business_goals ?? []).forEach((bg) => {
            (bg.plans ?? []).forEach((pl) => acc.push(pl));
          });
          return acc;
        }, [] as PlanCompact[]);

        const projectNameById = new Map<string, string>();
        const bgNameById = new Map<string, string>();
        const planNameById = new Map<string, string>();
        const scenarioNameById = new Map<string, string>();

        for (const p of projects) {
          projectNameById.set(String(p.id), String(p.name ?? ""));
          for (const bg of p.business_goals ?? []) {
            bgNameById.set(String(bg.id), String(bg.name ?? ""));
            for (const pl of bg.plans ?? []) {
              planNameById.set(String(pl.id), String(pl.name ?? ""));
              const scenarios = pl.scenarios ?? [];
              for (const s of scenarios) {
                scenarioNameById.set(s.id, s.name ?? "");
              }
            }
          }
        }

        const comparisons: ComparisonApiEntity[] = comparisonsResp.data ?? [];

        const tableRows = comparisons.map((c) => ({
          comparison_id: String(c.comparison_id),
          project_id: String(c.project_id),
          plan: planNameById.get(String(c.plan_id)) ?? "",
          scenarios: (c.scenario_ids ?? []).map((id) => scenarioNameById.get(String(id)) ?? id).join(";"),
          businessGoal: bgNameById.get(String(c.business_goal_id)) ?? "",
          project: projectNameById.get(String(c.project_id)) ?? "",
          dateOfComparison: formatUSDate(c.comparison_timestamp),
        }));

        ctx.commit("setComparisonHistory", tableRows);
      } catch (e) {
        console.error(e);
      } finally {
        ctx.commit("setLoading", false);
      }
    },
    async prepareComparisonData(ctx, payload: { projectId: string; scenarioIds: string[] }) {
      try {
        if (!payload.projectId || !payload.scenarioIds || payload.scenarioIds.length === 0) {
          console.warn("Project ID or scenario IDs are missing for comparison.", payload);
          ctx.commit("setSelectedScenarios", []);
          return;
        }

        ctx.commit("setLoading", true);
        const project: Project = await getProjectsApi().getProject(payload.projectId);

        ctx.state.projectDetailed = project;
        if (!project || !project.business_goals) {
          ctx.commit("setSelectedScenarios", []);
          return;
        }

        const wanted = new Set(payload.scenarioIds.map(String));
        const scenariosToCompare: Scenario[] = [];

        project.business_goals.forEach((bg) => {
          (bg.plans ?? []).forEach((pl) => {
            (pl.scenarios ?? []).forEach((sc) => {
              if (sc?.id && wanted.has(String(sc.id))) {
                scenariosToCompare.push(sc);
              }
            });
          });
        });

        ctx.commit("setSelectedScenarios", scenariosToCompare);
      } catch (e) {
        console.error("Error preparing comparison data:", e);
        ctx.commit("setSelectedScenarios", []);
      } finally {
        ctx.commit("setLoading", false);
      }
    },

    async createScenarioComparison(
      ctx: ActionContext<PlanListState, RootState>,
      payload: { projectId: string; businessGoalId: string; planId: string; scenarioNames: string[] }
    ) {
      ctx.commit("setLoading", true);
      try {
        const { planId, scenarioNames } = payload;
        const projects = ctx.state.projectsList || [];

        let projectId = "";
        let businessGoalId = "";
        let plan: PlanCompact | null = null;

        outer: for (const p of projects) {
          for (const bg of p.business_goals || []) {
            const found = (bg.plans || []).find((pl) => String(pl?.id) === String(planId));
            if (found) {
              projectId = String(p.id);
              businessGoalId = String(bg.id);
              plan = found;
              break outer;
            }
          }
        }

        const candidates = plan?.scenarios ?? [];
        const byName = new Map<string, string>();
        for (const s of candidates) {
          const name = (s?.name ?? "").trim().toLowerCase();
          const id = String(s?.id ?? "");
          if (name && id) byName.set(name, id);
        }

        const scenario_ids = Array.from(
          new Set(
            (scenarioNames || [])
              .map((n) => (n || "").trim().toLowerCase())
              .map((n) => byName.get(n))
              .filter((x): x is string => Boolean(x))
          )
        );

        return await getProjectsApi().postScenarioComparison({
          project_id: projectId,
          business_goal_id: businessGoalId,
          plan_id: planId,
          scenario_ids,
        });
      } finally {
        ctx.commit("setLoading", false);
      }
    },
  },

  getters: {
    isLoading: (state): boolean => state.loading,
    projectsList: (state): ProjectCompact[] => state.projectsList,
    plansList: (state): PlanCompact[] => state.plansList,
    selectedScenarios: (state): Scenario[] => state.selectedScenarios,
    projectDetailed: (state): Project => state.projectDetailed,
    comparisonHistory: (state): ComparisonHistoryEntity[] => state.comparisonHistory,
  },
};

export default planList;
